// Registers the root-scoped service worker and exposes the browser's native
// installation prompt only when that capability is actually available.
import { throwFrontendError } from './core/errors'
import { translate } from './core/i18n'
import { Log } from './core/logger'

type InstallChoice = {
  outcome: 'accepted' | 'dismissed'
  platform: string
}

type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>
  userChoice: Promise<InstallChoice>
}

let pendingInstallPrompt: BeforeInstallPromptEvent | null = null

/**
 * Returns every install control so responsive or future layouts stay synchronized.
 */
function installButtons(): HTMLButtonElement[] {
  return Array.from(
    document.querySelectorAll<HTMLButtonElement>('[data-pwa-install]'),
  )
}

/**
 * Shows or hides install controls without leaving an inactive menu action visible.
 */
function setInstallButtonsVisible(visible: boolean): void {
  installButtons().forEach((button) => {
    button.hidden = !visible
  })
}

/**
 * Presents the browser-owned install prompt in direct response to the user's click.
 */
async function promptForInstallation(): Promise<void> {
  const prompt = pendingInstallPrompt

  if (prompt === null) {
    return
  }

  pendingInstallPrompt = null
  setInstallButtonsVisible(false)

  await Log.notice('Present application install prompt', async () => {
    await prompt.prompt()
    await prompt.userChoice
  })
}

/**
 * Reports a failed native prompt without converting a normal dismissal into an error.
 */
function handleInstallButtonClick(): void {
  void promptForInstallation().catch((error: unknown) => {
    throwFrontendError(error, translate('pwa_install_failed'))
  })
}

/**
 * Retains the browser-owned prompt until the user selects the visible install action.
 */
function captureInstallPrompt(event: Event): void {
  Log.notice('Capture application install prompt', () => {
    event.preventDefault()
    pendingInstallPrompt = event as BeforeInstallPromptEvent

    setInstallButtonsVisible(true)
  })
}

/**
 * Removes the install action after the browser confirms installation.
 */
function clearInstallPrompt(): void {
  Log.notice('Complete application installation', () => {
    pendingInstallPrompt = null
    setInstallButtonsVisible(false)
  })
}

/**
 * Connects the account-menu action to the deferred browser install prompt.
 */
function initializeInstallControls(): void {
  const buttons = installButtons()

  buttons.forEach((button) => {
    button.addEventListener('click', handleInstallButtonClick)
  })

  window.addEventListener('beforeinstallprompt', captureInstallPrompt)
  window.addEventListener('appinstalled', clearInstallPrompt)
}

/**
 * Registers the generated worker only in a secure context with browser support.
 */
async function registerServiceWorker(): Promise<void> {
  if (!window.isSecureContext || !('serviceWorker' in navigator)) {
    return
  }

  const serviceWorkerUrl = document.body.dataset.serviceWorkerUrl

  if (serviceWorkerUrl === undefined || serviceWorkerUrl === '') {
    throw new Error('The service worker URL is missing from the page.')
  }

  await Log.notice('Register application service worker', () => {
    return navigator.serviceWorker.register(serviceWorkerUrl, {
      scope: '/',
      updateViaCache: 'none',
    })
  })
}

Log.info('Initialize application installation controls', () => initializeInstallControls())

window.addEventListener('load', () => {
  void registerServiceWorker().catch((error: unknown) => {
    throwFrontendError(error, translate('pwa_registration_failed'))
  })
})
